import { useUser } from '@/store'
import { useLogout, usePrivy, User } from '@privy-io/react-auth'

import { useEffect, useRef } from 'react'

/**
 * AUTHENTICATION AUTO-SYNC HOOK: Handles automatic authentication and validation
 *
 * This hook manages automatic authentication flows and state validation:
 * - Auto-triggers backend authentication when Privy authentication completes
 * - Validates authentication state consistency
 * - Handles cleanup for inconsistent states
 *
 * Usage: Import once at the router level for automatic authentication management
 *
 */
export const useAuth = () => {
  const { logout: privyLogout } = useLogout()
  const { user: privyUser, getAccessToken, ready } = usePrivy()
  const { auth, logout: storeLogout, isAuth } = useUser()

  // Prevent multiple simultaneous auth requests
  const authInProgress = useRef(false)
  // Track previous state to avoid unnecessary effect runs
  const prevAuthState = useRef<{ privyUser: User | null; isAuth: boolean; ready: boolean }>({
    privyUser: null,
    isAuth: false,
    ready: false,
  })

  /**
   * Auto-trigger backend authentication when Privy authentication completes
   * This creates a seamless one-click login experience
   * Only proceeds if user has accepted terms
   */
  useEffect(() => {
    const autoBackendAuth = async () => {
      // Check if state actually changed to avoid unnecessary runs
      const stateChanged =
        prevAuthState.current.privyUser !== privyUser ||
        prevAuthState.current.isAuth !== isAuth ||
        prevAuthState.current.ready !== ready

      // Update ref with current state
      prevAuthState.current = { privyUser, isAuth, ready }

      // Only trigger if state changed, Privy is authenticated but backend is not, and no auth in progress
      if (stateChanged && privyUser && !isAuth && ready && !authInProgress.current) {
        const hasAcceptedTerms = privyUser.hasAcceptedTerms

        if (!hasAcceptedTerms) {
          console.log('⚠️ User authenticated with Privy but terms not accepted - skipping backend auth')
          return
        }

        console.log('🔄 Auto-triggering backend auth after Privy login', {
          privyUserId: privyUser.id,
          hasAcceptedTerms,
        })
        authInProgress.current = true
        try {
          const privyToken = await getAccessToken()
          if (!privyToken) {
            console.error('❌ Failed to get Privy access token for auto auth')
            return
          }
          console.log('🔑 Auto backend auth with Privy token')
          await auth(undefined, privyToken)
        } catch (error) {
          console.error('❌ Auto backend auth failed:', error)
          // If auto backend auth fails, completely log out the user to avoid inconsistent state
          try {
            await privyLogout()
            await storeLogout()
          } catch (logoutError) {
            console.error('❌ Failed to logout after auto backend auth failure:', logoutError)
          }
        } finally {
          authInProgress.current = false
        }
      }
    }

    autoBackendAuth()
  }, [privyUser, isAuth, ready, getAccessToken, auth, privyLogout, storeLogout])

  /**
   * Simple validation: if user appears authenticated but has inconsistent state, logout
   */
  useEffect(() => {
    if (isAuth && ready) {
      const storedToken = window.localStorage.getItem('privy-token')

      // Case 1: No Privy session but appears authenticated
      if (!privyUser) {
        console.warn('⚠️ User appears authenticated but has no Privy session, logging out')
        storeLogout()
        return
      }

      // Case 2: Has Privy session but no stored token
      if (!storedToken) {
        console.warn('⚠️ User has Privy session but no stored token, logging out')
        storeLogout()
        return
      }
    }
  }, [isAuth, ready, privyUser, storeLogout])

  return {
    privyAuthenticated: !!privyUser && ready, // Whether user is authenticated with Privy
    isAuth, // Whether user is fully authenticated (Privy + Backend)
    ready, // Whether Privy SDK is ready for use
    hasAcceptedTerms: privyUser?.hasAcceptedTerms ?? false, // Whether user has accepted terms in Privy
  }
}
